/**
 * Transformers do módulo questionnaire-builder (form-builder):
 * QuestionnairePayload ⇄ fhir5.Questionnaire
 *
 * Objetivo:
 * - Manter o service focado em IO.
 * - Centralizar parsing e mapeamento em funções testáveis.
 */

import dayjs from "dayjs";
import {
  parseQuestionOptions,
  serializeQuestionOptions,
} from "src-next/shared/utils/questionnaireOptions";

import {
  QUESTIONNAIRE_METADATA,
  QUESTIONNAIRE_ORGANIZATION_IDENTIFIER_SYSTEM,
  QUESTIONNAIRE_USAGE_TYPE_EXTENSION_URL,
  USAGE_TYPE_TO_CODE,
  CODE_TO_USAGE_TYPE,
  QUESTION_TYPE_TO_FHIR,
  FHIR_TYPE_TO_QUESTION,
} from "./questionnaireQuestionnaireConstants";
import { QuestionnairePayload, QuestionnaireQuestionPayload } from "../types";

function slugFromOption(option: string): string {
  const trimmed = option.trim();
  if (!trimmed) return "";
  return trimmed
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

/** Exportado para alinhar respostas de múltipla escolha ao `answerOption` do Questionnaire. */
export function optionToSlugOrFallback(option: string, index: number): string {
  const slug = slugFromOption(option);
  return slug || `opcao-${index + 1}`;
}

/**
 * Pergunta vazia: label vazio ou apenas espaços (estado padrão ao criar).
 */
function isQuestionEmpty(question: QuestionnaireQuestionPayload): boolean {
  return (question.label?.trim() ?? "") === "";
}

/**
 * Filtra perguntas vazias, mantendo pelo menos uma para garantir recurso FHIR válido.
 */
function filterEmptyQuestions(
  questions: QuestionnaireQuestionPayload[],
): QuestionnaireQuestionPayload[] {
  const nonEmpty = questions.filter((q) => !isQuestionEmpty(q));
  if (nonEmpty.length > 0) {
    return nonEmpty;
  }
  const first = questions[0];
  return first
    ? [first]
    : [
        {
          id: "q-placeholder",
          label: "",
          type: "shortText" as const,
          isRequired: false,
          isPersistent: true,
        },
      ];
}

/**
 * Converte QuestionnairePayload para fhir5.Questionnaire.
 */
export function toFhirQuestionnaireFromPayload(input: {
  payload: QuestionnairePayload;
  status: "draft" | "active" | "unknown";
  existingId?: string;
  /**
   * Identifica a organização dona do questionário.
   *
   * Usado para compor:
   * - `identifier[0].value`  => `Organization/{organizationId}`
   * - `publisher`           => mesmo valor do `identifier`
   */
  organizationId: string;
}): fhir5.Questionnaire {
  const { payload, status, existingId, organizationId } = input;

  // FHIR R5 exige `identifier.system` e `identifier.value` para permitir recuperação futura.
  // `system` é fixo por design; apenas o `value` é dinâmico por organização.
  const organizationReference = `Organization/${organizationId}`;

  const questionsToPersist = filterEmptyQuestions(
    payload.questions.filter((q) => q.isPersistent !== false),
  );

  const items: fhir5.QuestionnaireItem[] = questionsToPersist.map(
    (question) => {
      const baseItem: fhir5.QuestionnaireItem = {
        linkId: question.id,
        text: question.label,
        type: QUESTION_TYPE_TO_FHIR[
          question.type
        ] as fhir5.QuestionnaireItem["type"],
        required: question.isRequired,
      };

      if (question.helperText) {
        baseItem.definition = question.helperText;
      }

      if (question.type === "multipleChoice") {
        const options = parseQuestionOptions(question.optionsCsv);
        baseItem.repeats = Boolean(question.allowMultipleAnswers);
        if (options.length > 0) {
          baseItem.answerOption = options.map((opt, idx) => ({
            valueCoding: {
              code: optionToSlugOrFallback(opt, idx),
              display: opt,
            },
          }));
        }
      }

      return baseItem;
    },
  );

  const questionnaire: fhir5.Questionnaire = {
    resourceType: "Questionnaire",
    meta: {
      lastUpdated: dayjs().toISOString(),
    },
    version: QUESTIONNAIRE_METADATA.defaultVersion,
    title: payload.title,
    status,
    publisher: organizationReference,
    identifier: [
      {
        system: QUESTIONNAIRE_ORGANIZATION_IDENTIFIER_SYSTEM,
        value: organizationReference,
      },
    ],
    contact: [...QUESTIONNAIRE_METADATA.contact],
    description: payload.description,
    copyright: QUESTIONNAIRE_METADATA.copyright,
    item: items,
    extension: [
      {
        url: QUESTIONNAIRE_USAGE_TYPE_EXTENSION_URL,
        valueCode: USAGE_TYPE_TO_CODE[payload.usageType],
      },
    ],
  };

  if (existingId) {
    questionnaire.id = existingId;
  }

  return questionnaire;
}

/**
 * Converte fhir5.Questionnaire para QuestionnairePayload.
 */
export function toPayloadFromFhirQuestionnaire(
  resource: fhir5.Questionnaire,
): QuestionnairePayload | null {
  if (resource.resourceType !== "Questionnaire") {
    return null;
  }

  const usageExtension = resource.extension?.find(
    (e) => e.url === QUESTIONNAIRE_USAGE_TYPE_EXTENSION_URL,
  );
  const usageCode = usageExtension?.valueCode;
  const usageType = (usageCode && CODE_TO_USAGE_TYPE[usageCode]) ?? "triage";

  const questions: QuestionnaireQuestionPayload[] = (resource.item ?? []).map(
    (item, index) => {
      const fhirType = item.type ?? "string";
      const questionType = FHIR_TYPE_TO_QUESTION[fhirType] ?? "shortText";

      let optionsCsv: string | undefined;
      let allowMultipleAnswers: boolean | undefined;
      if (questionType === "multipleChoice" && item.answerOption?.length) {
        optionsCsv = serializeQuestionOptions(
          item.answerOption
            .map((option) => {
              const display = option.valueCoding?.display?.trim();
              if (display) return display;
              return option.valueCoding?.code ?? "";
            })
            .filter(Boolean),
        );
      }
      if (questionType === "multipleChoice") {
        allowMultipleAnswers = Boolean(item.repeats);
      }

      return {
        id: item.linkId ?? `q-${index + 1}`,
        label: item.text ?? "",
        type: questionType,
        isRequired: Boolean(item.required),
        helperText: item.definition,
        optionsCsv,
        allowMultipleAnswers,
        isPersistent: true,
      };
    },
  );

  return {
    id: resource.id,
    title: resource.title ?? "",
    description: resource.description ?? "",
    usageType: usageType as
      | "triage"
      | "preConsultation"
      | "postConsultation"
      | "attendance",
    // Reduzimos para os status usados na UI; demais valores FHIR ficam indefinidos.
    status:
      resource.status === "active" ||
      resource.status === "draft" ||
      resource.status === "unknown"
        ? resource.status
        : undefined,
    questions,
  };
}
